import { useCallback } from 'react';

import { getFeatureDetails } from './getFeatureDetails';
import { parseConfig } from './parseConfig';
import { useGetAttributesWithValues } from './useGetAttributesWithValues';
import { useGetSplitClient } from './useGetSplitClient';

import type { Feature, FeatureReturns } from './features';

interface ControlTreatment {
  treatment: 'control';
  config: undefined;
  isReady: boolean;
}

const NOT_READY_CONTROL_TREATMENT = {
  treatment: 'control',
  config: undefined,
  isReady: false,
} as const satisfies ControlTreatment;

type FeatureReturn<F extends Feature> = {
  treatment: FeatureReturns[F]['treatment'];
  // config will be undefined if the JSON config fails to parse
  config: FeatureReturns[F]['config'] | undefined;
};

type GetTreatment = <F extends Feature>(
  feature: F
) => (FeatureReturn<F> & { isReady: true }) | ControlTreatment;

export function useGetTreatment(): GetTreatment {
  const getSplitClient = useGetSplitClient();
  const getAttributesWithValues = useGetAttributesWithValues();

  const getTreatment: GetTreatment = useCallback(
    <F extends Feature>(feature: F) => {
      const { trafficType, attributes } = getFeatureDetails(feature);

      const { client, isReady } = getSplitClient(trafficType);

      if (!client || !isReady) {
        return NOT_READY_CONTROL_TREATMENT;
      }

      const attributesWithValues =
        attributes && getAttributesWithValues(attributes);

      const { treatment, config: configJSON } = client.getTreatmentWithConfig(
        feature,
        attributesWithValues
      ) as {
        treatment: FeatureReturns[F]['treatment'];
        config: FeatureReturns[F]['config'] extends undefined ? null : string;
      };

      const config = configJSON ? parseConfig(feature, configJSON) : undefined;

      return {
        treatment,
        config,
        isReady,
      };
    },
    [getSplitClient, getAttributesWithValues]
  );

  return getTreatment;
}
